// Utility functions for sharing meeting summaries via URL parameters

export const encodeMeetingData = (meetingName, attendees, elapsedSeconds, finalCost, recurringData = null, sourceCalculator = 'live', currency = 'GBP', region = 'UK') => {
  const totalAttendees = Object.values(attendees).reduce((sum, count) => sum + count, 0);
  
  const meetingData = {
    meetingName: meetingName || null,
    totalAttendees,
    duration: {
      hours: Math.floor(elapsedSeconds / 3600),
      minutes: Math.floor((elapsedSeconds % 3600) / 60),
      seconds: elapsedSeconds % 60,
      totalSeconds: elapsedSeconds,
    },
    finalCost,
    attendeeBreakdown: attendees,
    sharedAt: new Date().toISOString(),
    sourceCalculator, // 'live' or 'meeting'
    currency: currency || 'GBP',
    region: region || 'UK',
    ...(recurringData && { recurring: recurringData }),
  };
  
  try {
    // Convert to JSON, then base64 encode
    const jsonString = JSON.stringify(meetingData);
    const encoded = btoa(unescape(encodeURIComponent(jsonString)));
    return encoded;
  } catch (error) {
    console.error('Error encoding meeting data:', error);
    return null;
  }
};

export const decodeMeetingData = (encodedData) => {
  try {
    // Decode base64, then parse JSON
    const jsonString = decodeURIComponent(escape(atob(encodedData)));
    const data = JSON.parse(jsonString);
    
    // Handle both meeting and document data
    if (data.dataType === 'document') {
      if (!validateDocumentData(data)) {
        return null;
      }
    } else {
      // Legacy meeting data or current meeting data
      if (!validateMeetingData(data)) {
        return null;
      }
      
      // Provide backward compatibility defaults for currency/region
      if (!data.currency) {
        data.currency = 'GBP';
      }
      if (!data.region) {
        data.region = 'UK';
      }
    }
    
    return data;
  } catch (error) {
    console.error('Error decoding data:', error);
    return null;
  }
};

export const validateMeetingData = (data) => {
  if (!data || typeof data !== 'object') return false;
  
  // Check required fields
  const requiredFields = ['totalAttendees', 'duration', 'finalCost', 'attendeeBreakdown'];
  for (const field of requiredFields) {
    if (!(field in data)) return false;
  }
  
  // Validate duration structure
  if (!data.duration || typeof data.duration !== 'object') return false;
  if (typeof data.duration.totalSeconds !== 'number') return false;
  
  // Validate numeric fields
  if (typeof data.totalAttendees !== 'number' || data.totalAttendees < 0) return false;
  if (typeof data.finalCost !== 'number' || data.finalCost < 0) return false;
  
  return true;
};

export const validateDocumentData = (data) => {
  if (!data || typeof data !== 'object') return false;
  
  // Check required fields for document data
  const requiredFields = ['documentTitle', 'finalCost', 'breakdown', 'participants', 'timeEstimates'];
  for (const field of requiredFields) {
    if (!(field in data)) return false;
  }
  
  // Validate numeric fields
  if (typeof data.finalCost !== 'number' || data.finalCost < 0) return false;
  if (typeof data.totalParticipants !== 'number' || data.totalParticipants < 0) return false;
  
  // Validate structure
  if (!data.breakdown || typeof data.breakdown !== 'object') return false;
  if (!data.participants || typeof data.participants !== 'object') return false;
  if (!data.timeEstimates || typeof data.timeEstimates !== 'object') return false;
  
  return true;
};

export const encodeDocumentData = (
  documentTitle, 
  documentType, 
  contentLength, 
  authors, 
  reviewers, 
  readers, 
  writingTimeHours, 
  reviewTimeHours, 
  readingTimeHours, 
  reviewCycles, 
  finalCost,
  breakdown
) => {
  const totalParticipants = 
    Object.values(authors).reduce((sum, count) => sum + count, 0) +
    Object.values(reviewers).reduce((sum, count) => sum + count, 0) +
    Object.values(readers).reduce((sum, count) => sum + count, 0);
  
  const documentData = {
    documentTitle: documentTitle || null,
    documentType,
    contentLength: Number(contentLength) || 0,
    totalParticipants,
    participants: {
      authors,
      reviewers,
      readers
    },
    timeEstimates: {
      writingTimeHours: Number(writingTimeHours) || 0,
      reviewTimeHours: Number(reviewTimeHours) || 0,
      readingTimeHours: Number(readingTimeHours) || 0,
      reviewCycles: Number(reviewCycles) || 0
    },
    finalCost,
    breakdown,
    sharedAt: new Date().toISOString(),
    sourceCalculator: 'document',
    dataType: 'document' // Distinguishes from meeting data
  };
  
  try {
    const jsonString = JSON.stringify(documentData);
    const encoded = btoa(unescape(encodeURIComponent(jsonString)));
    return encoded;
  } catch (error) {
    console.error('Error encoding document data:', error);
    return null;
  }
};

export const generateShareUrl = (encodedData) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/shared?data=${encodedData}`;
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (fallbackError) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

export const formatDuration = (duration) => {
  const { hours, minutes, seconds } = duration;
  const parts = [];
  
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  
  return parts.join(' ');
};

export const formatSharedDate = (isoString) => {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      return 'Unknown date';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return 'Unknown date';
  }
};